/**
 * Request tracking consumed by the DevRequestBar. Opt-in only:
 * enabled solely when the build/dev process runs with
 * `NEXT_PUBLIC_DEV_REQUEST_BAR=1` (e.g. in .env.local for `next dev`,
 * or on the `next build` command for local diagnostics of a production
 * build). Never set that flag for a deployed build — the server buffer
 * is process-global, so requests of all users would be visible to
 * anyone. When disabled, every call is a no-op and the inlined flag
 * lets bundlers drop the bar from client bundles.
 */

export type DevRequestSource = "server" | "client";

export type DevRequestEntry = {
  id: number;
  source: DevRequestSource;
  method: string;
  url: string;
  status: number;
  durationMs: number;
  /** Epoch ms of when the request started, used to merge/sort both sources. */
  startedAt: number;
};

const MAX_ENTRIES = 300;

export const isDevRequestBarEnabled =
  process.env.NEXT_PUBLIC_DEV_REQUEST_BAR === "1";

/**
 * Server-side ring buffer. Stashed on `globalThis` because in `next dev`
 * the same module can be instantiated once per compiled entrypoint —
 * a plain module-level variable would not be shared between RSC renders
 * and the /api/dev-requests route handler.
 */
type ServerBuffer = { seq: number; entries: DevRequestEntry[] };

const getServerBuffer = (): ServerBuffer => {
  const globalStash = globalThis as typeof globalThis & {
    __devRequestsBuffer?: ServerBuffer;
  };
  globalStash.__devRequestsBuffer ??= { seq: 0, entries: [] };
  return globalStash.__devRequestsBuffer;
};

export const getServerDevRequests = (): DevRequestEntry[] =>
  getServerBuffer().entries;

export const clearServerDevRequests = (): void => {
  getServerBuffer().entries = [];
};

/**
 * Client-side store, consumed via `useSyncExternalStore` in the DevRequestBar.
 */
let clientEntries: DevRequestEntry[] = [];
let clientSeq = 0;
const clientListeners = new Set<() => void>();

export const subscribeClientDevRequests = (listener: () => void) => {
  clientListeners.add(listener);
  return () => {
    clientListeners.delete(listener);
  };
};

export const getClientDevRequestsSnapshot = (): DevRequestEntry[] =>
  clientEntries;

export const clearClientDevRequests = (): void => {
  clientEntries = [];
  for (const listener of clientListeners) {
    listener();
  }
};

export const reportDevRequest = (
  entry: Omit<DevRequestEntry, "id" | "source">,
): void => {
  if (!isDevRequestBarEnabled) {
    return;
  }

  if (typeof window === "undefined") {
    const buffer = getServerBuffer();
    buffer.seq += 1;
    buffer.entries = [
      ...buffer.entries.slice(-(MAX_ENTRIES - 1)),
      { ...entry, id: buffer.seq, source: "server" },
    ];
    return;
  }

  clientSeq += 1;
  clientEntries = [
    ...clientEntries.slice(-(MAX_ENTRIES - 1)),
    { ...entry, id: clientSeq, source: "client" },
  ];
  for (const listener of clientListeners) {
    listener();
  }
};
